import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  Card,
  CardBody,
  CardFooter,
  Typography,
  Input,
  Textarea,
  Select,
  Option,
} from "@material-tailwind/react";
import provinces from "../../public/thai-address/provinces.json";
import contactStaffApi from "@/api/contactStaff";
import {
  DialogMode,
  NotificationDialog,
  TNotificationDialog,
} from "./NotificationDialog";
import ProvinceSelect from "./ProvinceSelect";
import recommenderService from "@/features/Recommender/services";

interface Props {
  isOpen: boolean;
  onClose: (value: boolean) => void;
}

type TContactStaff = {
  name: string;
  phoneNumber: string;
  province: string;
  timeDuration: string;
  message: string;
  idLine: string;
  knownUsFrom: string;
};
const defaultModel: TContactStaff = {
  name: "",
  phoneNumber: "",
  province: "",
  timeDuration: "",
  message: "",
  idLine: "",
  knownUsFrom: "",
};

const timeDurations = ["09:00 - 12:00 น.", "13:00 - 17:00 น."];

export function DialogContactStaff({ isOpen, onClose }: Props) {
  const [open, setOpen] = useState(isOpen);
  const [validTimeDuration, setValidTimeDuration] = useState(true);
  const [validProvince, setValidProvince] = useState(true);
  const [model, setModel] = useState<TContactStaff>(defaultModel);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogSettings, setDialogSettings] = useState<
    Omit<TNotificationDialog, "isOpen" | "onClose">
  >({
    title: "",
    description: "",
    mode: DialogMode.SUCCESS,
    closeBtnTitle: "",
    confirmBtnTitle: "",
    onConfirmBtn: () => {},
  });
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const handleOpen = () => {
    setModel(defaultModel);
    onClose(!open);
    setOpen((cur) => !cur);
  };

  const onSetModel = (key: keyof TContactStaff, value: string) => {
    setModel((prev) => ({ ...prev, [key]: value }));
  };

  const delay = async (ms: number) => new Promise((rs) => setTimeout(rs, ms));

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      setIsLoading(true);
      if (!model.province) {
        setValidProvince(false);
        return;
      }
      if (!model.timeDuration) {
        setValidTimeDuration(false);
        return;
      }
      const { status } = await contactStaffApi.create({
        ...model,
        uplinerId: recommenderService.getId() ?? 1,
      });
      if (status === 200) {
        await delay(2000);
        handleOpen();
        setDialogSettings({
          title: "ส่งข้อมูลสำเร็จ",
          mode: DialogMode.SUCCESS,
          confirmBtnTitle: "ตกลง",
          onCloseBtn: () => {
            setModel(defaultModel);
            setOpenDialog(false);
          },
          onConfirmBtn: () => {
            setOpenDialog(false);
          },
          enabledPressBackdrop: false,
        });
        setOpenDialog(true);
      } else {
        handleOpen();
        openErrorDialog();
      }
    } catch (error) {
      openErrorDialog();
    } finally {
      setIsLoading(false);
    }
  };

  const onCloseDialog = () => {
    setOpenDialog(false);
  };

  const openErrorDialog = () => {
    setDialogSettings({
      title: "มีบางอย่างผิดพลาด",
      description: "กรุณาทำรายการใหม่อีกครั้ง",
      mode: DialogMode.WARNING,
      confirmBtnTitle: "ตกลง",
      onConfirmBtn: () => {
        setOpenDialog(false);
      },
    });
    setOpenDialog(true);
  };

  return (
    <>
      <NotificationDialog
        mode={dialogSettings.mode}
        isOpen={openDialog}
        onClose={onCloseDialog}
        title={dialogSettings.title}
        description={dialogSettings.description}
        closeBtnTitle={dialogSettings.closeBtnTitle}
        onCloseBtn={dialogSettings.onCloseBtn}
        confirmBtnTitle={dialogSettings.confirmBtnTitle}
        onConfirmBtn={dialogSettings.onConfirmBtn}
        enabledPressBackdrop={dialogSettings.enabledPressBackdrop}
      />

      <Dialog
        size="xs"
        open={open}
        handler={handleOpen}
        className="bg-transparent shadow-none font-noto"
      >
        <form action="" onSubmit={onSubmit}>
          <Card className="mx-auto w-full max-w-[24rem]">
            <div className="max-h-[80vh] overflow-y-auto px-1">
              <CardBody className="flex flex-col gap-4">
                <Typography
                  variant="h4"
                  color="blue-gray"
                  className="font-noto"
                >
                  ให้เจ้าหน้าที่ติดต่อกลับ
                </Typography>
                <Typography className="-mb-2 font-noto" variant="h6">
                  ชื่อ
                </Typography>
                <Input
                  label="กรอกชื่อ"
                  size="lg"
                  required
                  value={model.name}
                  onChange={(e) => onSetModel("name", e.target.value)}
                  disabled={isLoading}
                />
                <Typography className="-mb-2 font-noto" variant="h6">
                  เบอร์มือถือ
                </Typography>
                <Input
                  label="กรอกเบอร์มือถือ"
                  required
                  size="lg"
                  minLength={10}
                  maxLength={10}
                  value={model.phoneNumber}
                  onChange={(e) => onSetModel("phoneNumber", e.target.value)}
                  disabled={isLoading}
                />
                <Typography className="-mb-2 font-noto" variant="h6">
                  Line ID
                </Typography>
                <Input
                  label="กรอกข้อมูล"
                  required
                  size="lg"
                  value={model.idLine}
                  onChange={(e) => onSetModel("idLine", e.target.value)}
                  disabled={isLoading}
                />
                <Typography className="-mb-2 font-noto" variant="h6">
                  รู้จักเรา ผ่านสื่อใด
                </Typography>
                <Input
                  label="กรอกข้อมูล"
                  required
                  size="lg"
                  value={model.knownUsFrom}
                  onChange={(e) => onSetModel("knownUsFrom", e.target.value)}
                  disabled={isLoading}
                />
                <ProvinceSelect
                  provinces={provinces}
                  value={model.province}
                  onChange={(value: string) => onSetModel("province", value)}
                />
                <Typography className="-mb-2 font-noto" variant="h6">
                  ข้อความเพิ่มเติม
                </Typography>
                <Textarea
                  label="ข้อความ"
                  value={model.message}
                  onChange={(e) => onSetModel("message", e.target.value)}
                  disabled={isLoading}
                />
                <Typography className="-mb-2 font-noto" variant="h6">
                  ให้ติดต่อกลับ วัน-เวลา ทำการ จันทร์-ศุกร์
                </Typography>
                <Select
                  size="lg"
                  label="เลือกเวลา"
                  error={!validTimeDuration}
                  value={model.timeDuration}
                  disabled={isLoading}
                  onChange={(v) => {
                    if (v) onSetModel("timeDuration", v);
                    setValidTimeDuration(true);
                  }}
                >
                  {timeDurations.map((option) => (
                    <Option key={option} value={option} className="font-noto">
                      {option}
                    </Option>
                  ))}
                </Select>
              </CardBody>
              <CardFooter className="pt-0">
                <Button
                  type="submit"
                  fullWidth
                  className="bg-[#188F46] font-noto"
                  disabled={isLoading}
                  name="contact-staff-submit"
                  aria-label="ให้เจ้าหน้าที่ติดต่อกลับ - ส่งคำขอให้เจ้าหน้าที่โทรกลับ"
                >
                  ให้เจ้าหน้าที่ติดต่อกลับ
                </Button>
              </CardFooter>
            </div>
          </Card>
        </form>
      </Dialog>
    </>
  );
}
